// src/components/ui/AudienceSizeEstimator.tsx

import React, { useEffect, useRef, useState } from "react";
import { Users, RefreshCw, Zap } from "lucide-react";
import { clsx } from "clsx";

interface AudienceSizeEstimatorProps {
  size:       number;
  isLoading?: boolean;
  breakdown?: { label: string; value: number; color: string }[];
}

function useCountUp(target: number, duration = 600) {
  const [current, setCurrent] = useState(target);
  const prevRef = useRef(target);
  const rafRef  = useRef<number>();

  useEffect(() => {
    const start = prevRef.current;
    const diff  = target - start;
    if (diff === 0) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCurrent(Math.round(start + diff * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(animate);
      else prevRef.current = target;
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return current;
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

export default function AudienceSizeEstimator({
  size,
  isLoading = false,
  breakdown,
}: AudienceSizeEstimatorProps) {
  const animated = useCountUp(size);
  const graphPct  = Math.min((size / 90_000_000) * 100, 100);

  return (
    <div className="card border-2 border-blue/20 bg-gradient-to-br from-white to-blue/5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue/10 flex items-center justify-center">
            <Users size={14} className="text-blue" />
          </div>
          <span className="text-xs font-semibold text-navy uppercase tracking-wide">
            Estimated Reach
          </span>
        </div>
        {isLoading && (
          <RefreshCw size={13} className="text-slate animate-spin" />
        )}
      </div>

      {/* Big number */}
      <div className={clsx("transition-opacity duration-200", isLoading && "opacity-40")}>
        <p className="text-3xl font-bold text-navy tabular leading-none">
          {fmt(animated)}
        </p>
        <p className="text-xs text-slate mt-1">
          unique persons · {((size / 90_000_000) * 100).toFixed(1)}% of total graph
        </p>
      </div>

      {/* Graph bar */}
      <div className="mt-3">
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue to-cyan h-2 rounded-full transition-all duration-700"
            style={{ width: `${graphPct}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-2xs text-slate">0</span>
          <span className="text-2xs text-slate">90M total graph</span>
        </div>
      </div>

      {/* Breakdown */}
      {breakdown && breakdown.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
          {breakdown.map((item, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className={clsx("w-2 h-2 rounded-full", item.color)} />
                <span className="text-xs text-slate">{item.label}</span>
              </div>
              <span className="text-xs font-medium text-gray-700 tabular">
                {fmt(item.value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Match quality */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-1.5">
        <Zap size={11} className="text-cyan" />
        <span className="text-xs text-slate">
          <span className="font-semibold text-cyan">91%</span> identity match rate
          · refreshes daily
        </span>
      </div>
    </div>
  );
}